import { UsersListResponse } from "src/api/UsersListResponse";
import { UsersFindResponse } from "../api/UsersFindResponse";
import apiClient from "./core/ApiClient";
import { User } from "src/api/User";
import WebUser from "src/auth/WebUser";
import { UsersCreateRequest } from "src/api/UsersCreateRequest";
import { UsersAccessRequest } from "@/api/UsersAccessRequest";
import { UsersUpdateRequest } from "@/api/UsersUpdateRequest";
import { UsersRelationResponse } from "@/api/UsersRelationResponse";

const toNumberArrayMap = (
  value: Readonly<Map<number, ReadonlyArray<number>>> | Record<string, number[]> | undefined
): Map<number, number[]> => {
  if (!value) return new Map();

  if (value instanceof Map) {
    return new Map(Array.from(value.entries()).map(([k, v]) => [k, [...v]]));
  }

  return new Map(
    Object.entries(value).map(([k, v]) => [Number(k), [...v]])
  );
};

const getMe = (token?: string): Promise<User> =>
  apiClient.get<UsersFindResponse>("/api/users/me", token).then((r) => r.user);

const find = (id: number): Promise<User> =>
  apiClient.get<UsersFindResponse>(`/api/users/${id}`).then((r) => r.user);

const listAll = (): Promise<UsersListResponse> =>
  apiClient.get<UsersListResponse>("/api/users/");

const listRecent = (): Promise<UsersListResponse> =>
  apiClient.get<UsersListResponse>("/api/users/recents");

const remove = (id: number): Promise<void> =>
  apiClient.delete<void>(`/api/users/${id}`);

const avatarUpload = (blob: any): Promise<void> => {
  // upload blob to server
  let formData = new FormData();
  formData.append("file", blob);
  return apiClient
    .upload("/api/users/avatar", formData)
    .then((_) => Promise.resolve());
};

const avatarUrl = (user?: WebUser | User): string => {
  if (!user) return "";
  // If the user has a 'data' property, assume it's a WebUser; otherwise, it's a User.
  const userId = "data" in user ? user.data?.id : user.id;
  const organizationId = "data" in user ? user.data?.organizationId : user.organizationId;
  return avatarUrlFrom(organizationId, userId);
};

const avatarUrlFrom = (organizationId: number, userId?: number): string => userId ? `${apiClient.baseUrl}/api/users/${organizationId}/${userId}/avatar` : "";

const add = (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  partnerId?: number | null
): Promise<void> => {
  let request: UsersCreateRequest = {
    email: email,
    password: password,
    passwordHint: null,
    firstName: firstName,
    lastName: lastName,
    partnerId: partnerId ?? null,
  };
  return apiClient.post<UsersCreateRequest, void>("/api/users", request);
};

export type UserAccessStatus = 'active' | 'disabled';
export type UserAccessRole = 'member' | 'admin' | 'extern';

const updateAccess = (id: number, status: UserAccessStatus, role: UserAccessRole): Promise<void> =>
  apiClient.put<UsersAccessRequest, void>(`/api/users/${id}/access`, { status, role }).catch(error => apiClient.handleError(error));

const update = (id: number, firstName: string, lastName: string): Promise<void> => {
  let request: UsersUpdateRequest = {
    firstName: firstName,
    lastName: lastName,
  };
  return apiClient.put<UsersUpdateRequest, void>(`/api/users/${id}/profile`, request);
};

// only managers reporting to
const listReportsTo = (): Promise<Map<number, number[]>> => {
  return apiClient
    .get<UsersRelationResponse>('/api/users/reports')
    .then(r => toNumberArrayMap(r.reportingTo as Readonly<Map<number, ReadonlyArray<number>>> | Record<string, number[]>));
};

// all, managers reporting to and direct reports
const listReports = (): Promise<UsersRelationResponse> => apiClient.get<UsersRelationResponse>('/api/users/reports');
const addReport = (managerId: number, reportId: number): Promise<void> => apiClient.put(`/api/users/${managerId}/reports/${reportId}`, undefined);
const deleteReport = (managerId: number, reportId: number): Promise<void> => apiClient.delete(`/api/users/${managerId}/reports/${reportId}`);


export const userService = {
  toNumberArrayMap,
  getMe,
  find,
  listAll,
  listRecent,
  remove,
  avatarUpload,
  avatarUrl,
  avatarUrlFrom,
  add,
  update,
  updateAccess,
  listReportsTo,
  listReports,
  addReport,
  deleteReport,
};
